import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { Controller, useWatch } from "react-hook-form";

import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { ConditionalField } from "~/components/ui/conditional-field";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { Textarea } from "~/components/ui/textarea";
import { useDiscoveryForm } from "~/lib/hooks/use-discovery-form";
import {
  FaithBasedCoachingSchema,
  faithBasedCoachingStep,
  faithIntegrationsField,
  otherFaithIntegrationField,
} from "~/lib/schemas/coach-discovery";
import type { FaithBasedCoachingForm } from "~/lib/schemas/coach-discovery";
import { createNoIndexMeta } from "~/lib/shared/meta";

export const Route = createFileRoute("/_public/discovery/steps/8/")({
  head: () =>
    createNoIndexMeta(
      "Step 8: Faith-Based Coaching – Dream Coach Match",
      "Define your faith-based coaching preferences in our discovery process",
    ),
  component: FaithBasedCoachingStep,
});

function FaithBasedCoachingStep() {
  const { form, handleNextStep, handlePreviousStep } = useDiscoveryForm({
    step: 8,
    schema: FaithBasedCoachingSchema,
  });
  const { handleSubmit, control, setFocus, setValue, formState } = form;
  const { errors, isSubmitting, isValid } = formState;

  const watchedFaithIntegration = useWatch({
    control,
    name: "faithIntegration",
    defaultValue: [],
  });
  const showOtherField = watchedFaithIntegration?.includes("Other") ?? false;

  // Clear otherFaithIntegration when "Other" is deselected
  React.useEffect(() => {
    if (!showOtherField) {
      setValue("otherFaithIntegration", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [showOtherField, setValue]);

  const onSubmit = (_data: FaithBasedCoachingForm) => {
    handleNextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title={faithBasedCoachingStep.title} />
      <DiscoveryStep.Card>
        <FormField error={errors.faithIntegration?.message}>
          <CheckboxGroupField
            name="faithIntegration"
            control={control}
            options={faithIntegrationsField.options || []}
            exclusiveOptions={["No preference"]}
            onTrigger={{
              Other: () => setFocus("otherFaithIntegration"),
            }}
          />
        </FormField>

        <ConditionalField show={showOtherField} indent className="mt-2">
          <FormField error={errors.otherFaithIntegration?.message}>
            <Controller
              name="otherFaithIntegration"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Textarea
                  value={field.value}
                  onValueChange={field.onChange}
                  maxLength={otherFaithIntegrationField.maxLength}
                  placeholder={otherFaithIntegrationField.placeholder}
                />
              )}
            />
          </FormField>
        </ConditionalField>
      </DiscoveryStep.Card>
      <DiscoveryStep.Control
        prev={handlePreviousStep}
        type="submit"
        disabled={!isValid}
        loading={isSubmitting}
      />
    </form>
  );
}
